import React, { PropsWithChildren } from 'react';
import PlainButtonOrLink, {
  Props as BaseButtonProps,
} from '../../../Buttons/PlainButtonOrLink';
import { useDropdownToggleProps } from '../../hooks/useDropdownToggleProps';
import type { ComponentRequiresAriaHasPopup } from '../types';
import ArrowDown from '../../../Icons/ArrowDown';
import { IconSize } from '../../../Icons/IconWrap';
import { ButtonSize } from '../../../Buttons/types';
import cn from 'classnames';

import s from './PopperTransparentToggleButton.scss';

export interface Props
  extends Omit<BaseButtonProps, 'aria-haspopup'>,
    ComponentRequiresAriaHasPopup {
  size: ButtonSize;
}

/**
 * A wrapper around our DS `PlainButton` that provides the props for a dropdown toggle.
 *
 * Note: This component is only the toggle! It doesn't contain a popover or dropdown. If you're looking for something that does,
 * you probably want a preset instead of this. Consider `IconButtonWithContextMenu`.
 *
 * Required contexts: `PopperDropdownContext`, `DropdownStateContext`
 */
const PopperSolidToggleButton: React.FC<PropsWithChildren<Props>> = props => {
  const necessaryToggleProps = useDropdownToggleProps();

  return (
    <div
      className={cn(
        s['root'],
        props.className,
        s[`size-${props.size}`],
        props.disabled && s['disabled'],
        props.loading && s['loading']
      )}
    >
      <PlainButtonOrLink
        {...necessaryToggleProps}
        {...props}
        className={s['toggle-button']}
      >
        <ArrowDown
          className={s['arrow-icon']}
          size={IconSize.MEDIUM}
          disableDefaultColors
        />
      </PlainButtonOrLink>
      <ArrowDown
        className={s['arrow-icon-overlay']}
        size={IconSize.MEDIUM}
        disableDefaultColors
      />
    </div>
  );
};

PopperSolidToggleButton.displayName = 'PopperTransparentToggleButton';
export default PopperSolidToggleButton;
